import { useQuery } from '@tanstack/react-query';

import { SELLER_TYPES } from 'Components/VehicleList/VehicleListFilters.consts';
import { getVehicles } from 'Services/api/vehicles';
import { logger } from 'Services/logger/logger';
import { useRecommendedSortOrderExperimentation } from 'Services/optimizely/experimentations/useRecommendedSortOrderExperimentation';
import type { VehicleListItem } from 'Types/vehicle';
import { LIST_TYPES } from 'Utilities/consts';

interface UseMobilizeRecommendationsParams {
	enabled: boolean;
	limit: number;
}

interface UseMobilizeRecommendationsResult {
	error: unknown;
	isLoading: boolean;
	vehicles: VehicleListItem[];
}

export const useMobilizeRecommendations = ({
	enabled,
	limit,
}: UseMobilizeRecommendationsParams): UseMobilizeRecommendationsResult => {
	const { recommendedSortOrderVersion, shouldIncludeRecommendedSortOrderVersion } =
		useRecommendedSortOrderExperimentation();

	const { data, error, isLoading } = useQuery({
		enabled,
		onError: (e: unknown) => {
			const message = 'There was an issue with fetching Mobilize recommendations';
			const wrapped = e instanceof Error ? e : new Error(message);
			logger.error({
				context: { extra: { info: message } },
				error: wrapped,
				message,
				scope: 'useMobilizeRecommendations',
			});
		},
		queryFn: async () => {
			const res = await getVehicles({
				listType: LIST_TYPES.auction,
				order: 'DESC',
				page: 0,
				sellerType: SELLER_TYPES.FLEET,
				sort: 'recommended',
				...(shouldIncludeRecommendedSortOrderVersion && recommendedSortOrderVersion
					? { recommendedSortOrderVersion }
					: {}),
			});
			return res?.vehicles?.slice(0, limit) ?? [];
		},
		queryKey: ['mobilize-recommendations', limit, recommendedSortOrderVersion],
	});

	return {
		error,
		isLoading: enabled && isLoading,
		vehicles: data ?? [],
	};
};
